import React, { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { bigBtn, choiceBtn, gcard } from "./gedKit";

function Result({ ok, right, wrong }) {
  if (ok == null) return null;
  return (
    <div data-testid="qt-result" className={`flex items-start gap-2 rounded-xl p-3 text-base ${ok ? "bg-emerald-500/15 text-emerald-100" : "bg-red-500/15 text-red-100"}`}>
      {ok ? <CheckCircle2 className="h-6 w-6 shrink-0" /> : <XCircle className="h-6 w-6 shrink-0" />}<span>{ok ? right : wrong}</span>
    </div>
  );
}

function MultipleChoice({ onCheck }) {
  const [pick, setPick] = useState(null);
  const opts = ["0.5", "0.25", "0.75", "0.6"];
  return (<>
    <p className="text-lg">Which number is the largest?</p>
    <div className="space-y-2">{opts.map((o) => (
      <button key={o} type="button" data-testid={`qt-mc-${o}`} onClick={() => setPick(o)} className={`${choiceBtn} ${pick === o ? "border-[#d4a017] bg-[#d4a017]/15" : "border-white/15"}`}>{o}</button>
    ))}</div>
    <button type="button" className={bigBtn} disabled={!pick} onClick={() => onCheck(pick === "0.75")} data-testid="qt-check">Check</button>
  </>);
}

function DragDrop({ onCheck }) {
  const chips = ["3/4", "1/4", "1/2"];
  const [slots, setSlots] = useState([null, null, null]);
  const [held, setHeld] = useState(null);
  const place = (i) => { if (!held) return setSlots(slots.map((s, j) => (j === i ? null : s))); setSlots(slots.map((s, j) => (j === i ? held : s === held ? null : s))); setHeld(null); };
  return (<>
    <p className="text-lg">Put the fractions in order from smallest to largest.</p>
    <p className="text-sm text-white/60">On the real test you drag. Here, tap a fraction, then tap a box.</p>
    <div className="flex gap-2">{chips.map((c) => (
      <button key={c} type="button" data-testid={`qt-chip-${c}`} onClick={() => setHeld(c)} disabled={slots.includes(c)}
        className={`min-h-[52px] flex-1 rounded-xl border-2 text-lg font-bold ${held === c ? "border-[#d4a017] bg-[#d4a017]/20" : "border-white/20"} disabled:opacity-25`}>{c}</button>
    ))}</div>
    <div className="flex gap-2">{slots.map((s, i) => (
      <button key={i} type="button" data-testid={`qt-slot-${i}`} onClick={() => place(i)} className="min-h-[64px] flex-1 rounded-xl border-2 border-dashed border-white/30 text-lg font-bold">{s || <span className="text-white/30 text-sm">{i === 0 ? "Smallest" : i === 2 ? "Largest" : ""}</span>}</button>
    ))}</div>
    <button type="button" className={bigBtn} disabled={slots.includes(null)} onClick={() => onCheck(slots.join() === "1/4,1/2,3/4")} data-testid="qt-check">Check</button>
  </>);
}

function FillBlank({ onCheck }) {
  const [v, setV] = useState("");
  return (<>
    <p className="text-lg">Type the missing number.</p>
    <div className="flex items-center gap-3 text-3xl font-bold">12 + <input inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} data-testid="qt-fill-input"
      className="w-24 rounded-xl bg-black/40 border-2 border-white/30 px-3 py-2 text-center outline-none focus:border-[#d4a017]" /> = 20</div>
    <button type="button" className={bigBtn} disabled={!v} onClick={() => onCheck(v.trim() === "8")} data-testid="qt-check">Check</button>
  </>);
}

function DropDown({ onCheck }) {
  const [v, setV] = useState("");
  return (<>
    <p className="text-lg">Pick the word that makes the sentence correct.</p>
    <p className="text-xl leading-loose">She <select value={v} onChange={(e) => setV(e.target.value)} data-testid="qt-select"
      className="mx-1 rounded-lg bg-black/60 border-2 border-[#d4a017] px-3 py-2 text-lg [color-scheme:dark]">
      <option value="">Select…</option><option>go</option><option>goes</option><option>going</option></select> to work every morning.</p>
    <button type="button" className={bigBtn} disabled={!v} onClick={() => onCheck(v === "goes")} data-testid="qt-check">Check</button>
  </>);
}

function Hotspot({ onCheck }) {
  const [pick, setPick] = useState(null);
  return (<>
    <p className="text-lg">Tap the point on the number line that shows 3.</p>
    <svg viewBox="0 0 320 90" className="w-full" data-testid="qt-hotspot">
      <line x1="20" y1="45" x2="300" y2="45" stroke="white" strokeWidth="3" />
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <g key={n} onClick={() => setPick(n)} className="cursor-pointer" data-testid={`qt-hot-${n}`}>
          <circle cx={20 + n * 56} cy="45" r="16" fill={pick === n ? "#d4a017" : "rgba(255,255,255,0.12)"} stroke="white" strokeWidth="2" />
          {(n === 0 || n === 5) && <text x={20 + n * 56} y="84" fill="white" fontSize="16" textAnchor="middle">{n}</text>}
        </g>
      ))}
    </svg>
    <button type="button" className={bigBtn} disabled={pick == null} onClick={() => onCheck(pick === 3)} data-testid="qt-check">Check</button>
  </>);
}

const TYPES = [
  ["Multiple choice", "Pick one answer from the list.", MultipleChoice, "Right. 0.75 is the biggest.", "Look again: 0.75 is bigger than 0.6."],
  ["Drag and drop", "Move items into the right spots.", DragDrop, "Perfect order: 1/4, 1/2, 3/4.", "Hint: 1/4 is a quarter, 1/2 is a half, 3/4 is three quarters."],
  ["Fill in the blank", "Type your own answer in the box.", FillBlank, "Yes, 12 + 8 = 20.", "Count up from 12 to 20. That's 8."],
  ["Drop-down menu", "Open the menu and choose.", DropDown, "Correct: 'She goes to work.'", "With 'she', we say 'goes'."],
  ["Hotspot", "Tap the right spot on a picture.", Hotspot, "Nice. That's 3.", "Count the dots from 0: 0, 1, 2, 3."],
];

export default function QuestionTypes({ voice, onDone }) {
  const [i, setI] = useState(0);
  const [ok, setOk] = useState(null);
  const [label, sub, Q, right, wrong] = TYPES[i];
  const check = (v) => { setOk(v); voice.play(v ? "right" : "wrong"); };
  const next = () => { setOk(null); i + 1 < TYPES.length ? setI(i + 1) : onDone(); };
  return (
    <div className="space-y-4" data-testid="lp-part-how_test">
      <div className="flex gap-1">{TYPES.map((t, j) => <span key={t[0]} className={`h-2 flex-1 rounded-full ${j < i ? "bg-emerald-500" : j === i ? "bg-[#d4a017]" : "bg-white/15"}`} />)}</div>
      <div className={`${gcard} space-y-4`} key={i}>
        <div><div className="text-xs uppercase tracking-[0.25em] text-[#d4a017]">{i + 1} of 5 · {label}</div><p className="text-sm text-white/60 mt-1">{sub}</p></div>
        <Q onCheck={check} />
        <Result ok={ok} right={right} wrong={wrong} />
        {ok != null && <button type="button" onClick={next} className={bigBtn} data-testid="qt-next">{i + 1 < TYPES.length ? "Next question type" : "Finish Part 1"}</button>}
      </div>
    </div>
  );
}
